import React from 'react'

import clsx from 'clsx'

import ActionLogger from '$extensionSrc/components/ActionLogger'
import AppleMailIcon from '$extensionSrc/icons/apple-mail-icon.svg'
import AutomationIcon from '$extensionSrc/icons/automation-icon.svg'
import BackArrowIcon from '$extensionSrc/icons/back-arrow-icon.svg'
import CheckPhotosIcon from '$extensionSrc/icons/check-photos-icon.svg'
import CheckmarkIcon from '$extensionSrc/icons/checkmark-icon.svg'
import CloseIcon from '$extensionSrc/icons/close-icon.svg'
import EmailIcon from '$extensionSrc/icons/email-icon.svg'
import GeneralOauthIcon from '$extensionSrc/icons/general-oauth.svg'
import GmailIcon from '$extensionSrc/icons/gmail-icon.svg'
import PauseIcon from '$extensionSrc/icons/pause-icon-no-bg.svg'
import PlayIcon from '$extensionSrc/icons/play-icon-no-bg.svg'
import SectionCollapseIcon from '$extensionSrc/icons/section-collapse-icon.svg'
import SectionExpandIcon from '$extensionSrc/icons/section-expand-icon.svg'
import SettingsCheckIcon from '$extensionSrc/icons/settings-check-icon.svg'
import UndoIcon from '$extensionSrc/icons/undo-icon.svg'
import UpgradeLock from '$extensionSrc/icons/upgrade-lock-sparkle.svg'

import './ActionButton.scss'

export enum ActionButtonSize {
  EXTRA_SMALL = 'EXTRA_SMALL',
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

/**
 * Modifies color of some action button types
 */
export enum ActionButtonColor {
  GREEN = 'GREEN',
  PURPLE = 'PURPLE',
}

export enum ActionButtonType {
  PRIMARY = 'PRIMARY',
  SECONDARY = 'SECONDARY',
  SECONDARY_ROW = 'SECONDARY_ROW',
  TERTIARY = 'TERTIARY',
  TERTIARY_BORDER = 'TERTIARY_BORDER',
  WARNING = 'WARNING',
  DANGER = 'DANGER',
}

export enum ActionButtonIcon {
  APPLE_MAIL = 'apple-mail-icon',
  AUTOMATION = 'automation-icon',
  BACK_ARROW = 'back-arrow-icon',
  CANCEL = 'cancel-icon',
  CHECKMARK = 'checkmark-icon',
  GMAIL = 'gmail-icon',
  PAUSE = 'pause-icon',
  PHOTOS_CHECK = 'photos-check-icon',
  PLAY = 'play-icon',
  SECTION_COLLAPSE = 'section-collapse',
  SECTION_EXPAND = 'section-expand',
  SETTINGS_CHECK = 'settings-check-icon',
  UNDO = 'undo-icon',
  UPGRADE_LOCK = 'upgrade-lock-icon',
  EMAIL = 'email-icon',
  GENERAL_OAUTH = 'general-oauth-icon',
}

const iconMap: Record<
  ActionButtonIcon,
  React.FunctionComponent<React.SVGProps<SVGSVGElement>>
> = {
  [ActionButtonIcon.AUTOMATION]: AutomationIcon,
  [ActionButtonIcon.BACK_ARROW]: BackArrowIcon,
  [ActionButtonIcon.CHECKMARK]: CheckmarkIcon,
  [ActionButtonIcon.UNDO]: UndoIcon,
  [ActionButtonIcon.CANCEL]: CloseIcon,
  [ActionButtonIcon.SETTINGS_CHECK]: SettingsCheckIcon,
  [ActionButtonIcon.PHOTOS_CHECK]: CheckPhotosIcon,
  [ActionButtonIcon.GMAIL]: GmailIcon,
  [ActionButtonIcon.APPLE_MAIL]: AppleMailIcon,
  [ActionButtonIcon.PAUSE]: PauseIcon,
  [ActionButtonIcon.PLAY]: PlayIcon,
  [ActionButtonIcon.SECTION_EXPAND]: SectionExpandIcon,
  [ActionButtonIcon.SECTION_COLLAPSE]: SectionCollapseIcon,
  [ActionButtonIcon.UPGRADE_LOCK]: UpgradeLock,
  [ActionButtonIcon.EMAIL]: EmailIcon,
  [ActionButtonIcon.GENERAL_OAUTH]: GeneralOauthIcon,
}

const iconLabelMap: Record<ActionButtonIcon, string> = {
  [ActionButtonIcon.AUTOMATION]: 'Automate',
  [ActionButtonIcon.BACK_ARROW]: 'Back',
  [ActionButtonIcon.CHECKMARK]: 'Checkmark',
  [ActionButtonIcon.UNDO]: 'Undo',
  [ActionButtonIcon.CANCEL]: 'Close',
  [ActionButtonIcon.SETTINGS_CHECK]: 'Check Settings',
  [ActionButtonIcon.PHOTOS_CHECK]: 'Check Photos',
  [ActionButtonIcon.GMAIL]: 'Gmail',
  [ActionButtonIcon.APPLE_MAIL]: 'Apple Mail',
  [ActionButtonIcon.PAUSE]: 'Pause',
  [ActionButtonIcon.PLAY]: 'Play',
  [ActionButtonIcon.SECTION_EXPAND]: 'Expand',
  [ActionButtonIcon.SECTION_COLLAPSE]: 'Collapse',
  [ActionButtonIcon.UPGRADE_LOCK]: 'Lock',
  [ActionButtonIcon.EMAIL]: 'Email',
  [ActionButtonIcon.GENERAL_OAUTH]: 'OAuth',
}

export default function ActionButton({
  ariaLabel,
  children,
  className = '',
  color,
  disabled = false,
  iconBorder = false,
  iconName,
  iconOnly = false,
  onClick = () => {},
  onMouseEnter = () => {},
  onMouseLeave = () => {},
  role,
  size = ActionButtonSize.SMALL,
  type = ActionButtonType.PRIMARY,
  htmlType = 'button',
}: React.PropsWithChildren<{
  ariaLabel?: string
  className?: string
  color?: ActionButtonColor
  disabled?: boolean
  iconBorder?: boolean
  iconName?: ActionButtonIcon
  iconOnly?: boolean
  onClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  role?: string
  size?: ActionButtonSize
  type?: ActionButtonType
  htmlType?: 'button' | 'submit' | 'reset'
}>) {
  // Get the text of the button in a way that appropriately reacts to any changes
  const [buttonTextContent, setButtonTextContent] = React.useState<
    string | null
  >(null)
  const buttonTextGetterRef = React.useCallback((node) => {
    if (node) {
      setButtonTextContent(node.textContent)
    }
  }, [])

  const baseClass = {
    [ActionButtonType.PRIMARY]: 'btn-primary',
    [ActionButtonType.SECONDARY]: 'btn-secondary',
    [ActionButtonType.SECONDARY_ROW]: 'btn-secondary row',
    [ActionButtonType.TERTIARY]: 'btn-tertiary',
    [ActionButtonType.TERTIARY_BORDER]: 'btn-tertiary with-border',
    [ActionButtonType.WARNING]: 'btn-warning',
    [ActionButtonType.DANGER]: 'btn-danger',
  }[type]

  // Note: will only modify certain classes (see style sheet for detail)
  let colorModifierClass: string
  if (color) {
    colorModifierClass = {
      [ActionButtonColor.GREEN]: 'green',
      [ActionButtonColor.PURPLE]: 'purple',
    }[color]
  }

  const sizeModifierClass = {
    [ActionButtonSize.EXTRA_SMALL]: 'extra-small',
    [ActionButtonSize.SMALL]: 'small',
    [ActionButtonSize.MEDIUM]: 'medium',
    [ActionButtonSize.LARGE]: 'large',
  }[size]

  let icon
  if (iconName) {
    const iconClass = clsx(iconName, 'btn-icon', iconBorder && 'bordered')

    const IconComponent = iconMap[iconName]

    const iconLabel = iconLabelMap[iconName]

    if (iconOnly) {
      icon = <IconComponent aria-label={iconLabel} className={iconClass} />
    } else {
      icon = <IconComponent aria-hidden className={iconClass} />
    }
  }

  const allClassNames = clsx(
    'ActionButton',
    className,
    'btn',
    'override',
    baseClass,
    colorModifierClass,
    sizeModifierClass,
    iconName && !iconOnly ? 'has-icon' : '',
    iconOnly ? 'icon-only' : '',
  )

  return (
    <ActionLogger
      eventName="button:click"
      buttonTextContent={buttonTextContent?.toString().toLowerCase()}
      getDerivedProps={() => {}}
      actionProps={
        buttonTextContent ? ['onClick', buttonTextContent] : ['onClick']
      }
      label={ariaLabel}
    >
      <button
        aria-label={ariaLabel}
        className={allClassNames}
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={disabled}
        ref={buttonTextGetterRef}
        role={role}
        type={htmlType}
      >
        {icon}
        {iconOnly ? null : children}
      </button>
    </ActionLogger>
  )
}
